import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { Link } from "react-router-dom";
import { normalizeApiBase, supportApi } from "./supportApi";
import "./SupportInbox.css";
import SupportAdminCall from "./SupportAdminCall";
import { unlockCallRingtone, removeLegacyCallPush } from "./callAlerts";

const Context = createContext(null);

export function SupportAlertsProvider({
  children,
  apiUrl,
  token: suppliedToken,
}) {
  // If your admin stores JWT under a different key, supply `token` explicitly.
  const [fallbackToken] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem("token") || "" : "",
  );
  const token = String(
    suppliedToken === undefined ? fallbackToken : suppliedToken || "",
  ).replace(/^Bearer\s+/i, "");
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const soundEnabled = useRef(false);
  const [soundOn, setSoundOn] = useState(false);

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [outgoingCall, setOutgoingCall] = useState(null);
  const outgoingSequence = useRef(0);
  const [lastAlert, setLastAlert] = useState(null);
  const [error, setError] = useState("");

  const refreshUnread = useCallback(async () => {
    if (!token || !apiUrl) return;
    try {
      const data = await supportApi({
        apiUrl,
        token,
        path: "/agents/conversations",
      });
      setUnread(
        (data.conversations || []).reduce(
          (sum, chat) => sum + (chat.unreadForAgent || 0),
          0,
        ),
      );
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [apiUrl, token]);

  const enableAlerts = async () => {
    try {
      await unlockCallRingtone();
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        audioRef.current ||= new Ctx();
        await audioRef.current.resume(); // only called inside user click
        soundEnabled.current = true;
        setSoundOn(true);
      }
    } catch (err) {
      setError(`Could not enable ringtone: ${err.message}`);
    }
  };
  const playPing = () => {
    const context = audioRef.current;
    if (!soundEnabled.current || !context || context.state !== "running")
      return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      660,
      context.currentTime + 0.12,
    );
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.14, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.24);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.25);
  };

  useEffect(() => {
    if (!token || !apiUrl) return;
    let origin;
    try {
      origin = normalizeApiBase(apiUrl);
    } catch (err) {
      setError(err.message);
      return;
    }
    const client = io(origin, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 750,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = client;
    setSocket(client);
    client.on("connect", () => {
      setConnected(true);
      setError("");
      void refreshUnread();
    });
    client.on("disconnect", () => setConnected(false));
    client.on("connect_error", (err) => {
      setConnected(false);
      setError(`Support socket: ${err.message}`);
    });
    client.on("support:conversation_changed", () => void refreshUnread());
    client.on("support:agent_notification", (incoming) => {
      setLastAlert({ ...incoming, nonce: Date.now() });
      void refreshUnread();
      playPing();
    });
    return () => {
      client.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [token, apiUrl, refreshUnread]);

  const callCustomer = useCallback(
    async (conversationId, kind = "audio") => {
      if (!conversationId) throw new Error("Select a customer conversation.");
      if (!socket || !token)
        throw new Error("Admin support socket missing. Check login JWT.");
      // This is one single admin Socket.IO connection shared by the whole app.
      if (!socket.connected) {
        socket.connect();
        await new Promise((resolve, reject) => {
          if (socket.connected) {
            resolve();
            return;
          }
          const timer = setTimeout(() => {
            done();
            reject(
              new Error(
                "Support is offline. Could not reconnect within 12 seconds.",
              ),
            );
          }, 12000);
          const done = () => {
            clearTimeout(timer);
            socket.off("connect", ready);
            socket.off("connect_error", failed);
          };
          const ready = () => {
            done();
            resolve();
          };
          const failed = (err) => {
            done();
            reject(new Error(`Socket authentication/network: ${err.message}`));
          };
          socket.once("connect", ready);
          socket.once("connect_error", failed);
        });
      }
      // Check actual customer presence using the authenticated backend room.
      const status = await supportApi({
        apiUrl,
        token,
        path: `/agents/conversations/${encodeURIComponent(conversationId)}/call-status`,
      });
      if (status.inGame)
        throw new Error(
          "Customer is on the Ludo game screen. Calling is paused until they leave the game.",
        );
      if (!status.online)
        throw new Error(
          "Customer is offline. They must keep Ludobro open to receive the call.",
        );
      if (kind === "video" && !status.videoAllowed)
        throw new Error("Video permission is not enabled for this customer.");
      await unlockCallRingtone().catch(() => {});
      outgoingSequence.current += 1;
      setOutgoingCall({
        conversationId,
        kind,
        sequence: outgoingSequence.current,
      });
    },
    [socket, token, apiUrl],
  );

  // Unlock in-page audio after the first real user gesture; no Notification API.
  useEffect(() => {
    void removeLegacyCallPush().catch(() => {});
    const unlock = () => {
      void unlockCallRingtone().catch(() => {});
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        audioRef.current ||= new Ctx();
        void audioRef.current
          .resume()
          .then(() => {
            soundEnabled.current = true;
            setSoundOn(true);
          })
          .catch(() => {});
      }
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);
  useEffect(
    () => () => {
      audioRef.current?.close?.();
    },
    [],
  );
  return (
    <Context.Provider
      value={{
        apiUrl,
        token,
        socket,
        connected,
        unread,
        lastAlert,
        error,
        refreshUnread,
        enableAlerts,
        soundOn,
        callCustomer,
      }}
    >
      {children}
      <SupportAdminCall
        socket={socket}
        token={token}
        apiUrl={apiUrl}
        outgoingRequest={outgoingCall}
      />
    </Context.Provider>
  );
}

export function useSupportAlerts() {
  const value = useContext(Context);
  if (!value) throw new Error("Wrap the admin app with SupportAlertsProvider.");
  return value;
}

// Place once inside your admin navbar; provider stays mounted across routes.
export function SupportAlertBell({ href = "/admin/support" }) {
  const { unread, connected } = useSupportAlerts();
  return (
    <Link className="gp-alert-bell" to={href} title="Customer Support Inbox">
      <span aria-hidden="true">🎧</span>
      <span>Support</span>
      {unread > 0 && (
        <b className="gp-alert-count">{unread > 99 ? "99+" : unread}</b>
      )}
      <span className={`gp-dot ${connected ? "gp-dot-on" : ""}`} />
    </Link>
  );
}
